/**
 * Which org, if any, the signed-in session may act as.
 *
 * Nothing the client sends scopes a request to an org — the gateway derives it
 * from the token's org_id claim, falling back to the user's memberships. A
 * brand-new signup's token carries NO org_id (verified against prod: the access
 * token holds sub/email/realm_access and nothing else), so until a membership
 * row exists the gateway can resolve no org and every scoped route answers
 * 401 {"error":"Authentication required"}.
 *
 * For most signups GET /users/me/memberships auto-provisions that row on the
 * first call and the window closes. For some it never does — a competitor-domain
 * signup is deliberately given no workspace at all ("Block competitor signups —
 * no workspace, flagged for review"), so the endpoint answers a perfectly
 * healthy 200 {"data":[],"pending":[]} and the account has no org forever.
 *
 * That last case is what this exists for. The dashboard used to treat it as
 * unremarkable and render the full shell, where every call 401'd and the
 * connectors page relabelled those 401s "Session expired. Please refresh the
 * page." — untrue (the session is fine), unactionable, and unchanged by the
 * refresh it asks for. Resolving the state explicitly is what lets the app show
 * one honest terminal screen instead.
 */

export type OrgStatus =
  /** Memberships not loaded yet, or a retry is still outstanding. */
  | "loading"
  /** An org resolved — render the app. */
  | "ready"
  /** No active membership, but at least one awaiting an admin's approval. */
  | "pending_approval"
  /** Loaded cleanly, and this account has no org. Terminal. */
  | "none"
  /** The memberships request itself failed, so we do not know. Terminal. */
  | "error";

export interface OrgStatusInput {
  /**
   * Sessions that never fetch memberships: scoped link tokens, preview,
   * AUTH_DISABLED. This flag plus the `platform_admin` check below are the
   * WHOLE exemption surface — anything else that legitimately operates without
   * an org has to be added here, and will not find this file by searching for
   * its own name.
   */
  bypass: boolean;
  /**
   * The session's resolved role. `platform_admin` operates across orgs and may
   * hold no org of its own, so it is exempt from the terminal states below.
   */
  role: string | undefined;
  /** The memberships request has settled at least once (ok or not). */
  loaded: boolean;
  /** The last memberships request failed or answered non-2xx. */
  failed: boolean;
  /** More bounded retries are still scheduled. */
  retrying: boolean;
  /** userContext.orgId — "" when the token carried no org_id and none was adopted. */
  orgId: string | undefined;
  activeCount: number;
  pendingCount: number;
}

export function resolveOrgStatus(input: OrgStatusInput): OrgStatus {
  // These sessions are authorised by the token alone and are not permitted to
  // call the memberships API, so "no memberships" says nothing about them.
  if (input.bypass) return "ready";

  // platform_admin operates ACROSS orgs by design, so holding none of its own is
  // not an error state for it — it is the normal shape. CLAUDE.md rule 19
  // records that a prod platform_admin carries no `org_id` claim at all after
  // the account-switcher cutover, and that this exact shape already rendered the
  // separate admin console blank. Without this exemption, a change whose whole
  // purpose is to help accounts that have no org would lock out the one role
  // that legitimately has none, across the entire dashboard.
  if (input.role === "platform_admin") return "ready";

  if (!input.loaded) return "loading";

  // Either is enough: orgId can be adopted from a membership, and a membership
  // can exist before userContext catches up.
  if (input.orgId || input.activeCount > 0) return "ready";

  // Ahead of the retry wait, because this is already a definitive answer: an
  // invite awaiting approval is the account's actual state, not a slow read.
  // Behind it, an invited user would sit on a spinner for the whole retry
  // budget before reaching the page that explains their situation.
  if (input.pendingCount > 0) return "pending_approval";

  // A slow provision is the common case for a brand-new account, so stay in
  // loading while attempts remain rather than flashing a terminal screen at
  // someone whose org is seconds away.
  if (input.retrying) return "loading";

  // Checked before `none`: a failed request means we never learned whether this
  // account has an org, and saying "you have no workspace" on a network blip
  // would be a guess presented as a fact.
  if (input.failed) return "error";

  return "none";
}
